import React, { useRef, useLayoutEffect, useState, useCallback, useMemo, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { gsap } from 'gsap';
import * as PIXI from 'pixi.js';
import { Container, Sprite } from '@pixi/react';
import { isoStandard } from '../../../../../core/helpers/isoStandard';
import { Spine } from '../../../../../core/classes/spine';
import { isMobile } from '../../../../../core/helpers/isMobile';
import { applicationStore } from '../../../../../store/applicationStore';
import { boostedJackpotStore } from '../../../../../store/boostedJackpotStore';
import { jackPotStore } from '../../../../../store/jackPotStore';
import { gameService } from '../../../../../services/gameService';
import { soundService, SoundTypes } from '../../../../../services/soundService';
import { isIChancy } from '../../../../../core/constants/parnerIds';
import { betReplayService } from '../../../../../services/betReplayService';
import { userInfoStore } from '../../../../../store/userInfoStore';

let wheelAnimation = null;
let wheelStopAnimationGsap = null;
const BoostedJackpotWheel = observer(
  ({ sectors, cursor, wheelValues, wheelBorder, wheelContainerRef, handleWheelScaleDefault }) => {
    const { isReplay } = userInfoStore;
    const { jackPotExpireTime } = jackPotStore;
    const { setIsShowBlackLayer } = applicationStore;
    const { sectorStopIndex, setIsBoostedJackpotWinPopup, setSectorStopIndex, spinWheelWinAmount } =
      boostedJackpotStore;
    const canvasRef = useRef(null);
    const wheelRef = useRef(null);
    const wheelLampRef = useRef(null);
    const wheelBlueBoardRef = useRef(null);
    const wheelSpinRef = useRef(null);

    const [texture, setTexture] = useState(null);
    const [spinClick, setSpinClick] = useState(true);

    const radius = 300;
    const colors = useMemo(() => ['#3855E5', '#ffffff'], []);
    const gradientColors = useMemo(
      () => [
        { offset: 0, color: '#BB9336' },
        { offset: 0.05, color: '#CBAB5A' },
        { offset: 0.16, color: '#E7D69B' },
        { offset: 0.24, color: '#F8F2C4' },
        { offset: 0.27, color: '#FFFCF3' },
        { offset: 0.32, color: '#FDF9CF' },
        { offset: 0.35, color: '#F5EFC4' },
        { offset: 0.39, color: '#E9DFB2' },
      ],
      []
    );

    useEffect(() => {
      gameService.sendWorker(jackPotExpireTime);
      return () => gameService.sendWorker();
    }, []);

    useLayoutEffect(() => {
      const createCanvas = document.createElement('canvas');
      createCanvas.width = 570;
      createCanvas.height = 570;
      canvasRef.current = createCanvas;
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const center = { x: canvas.width / 2, y: canvas.height / 2 };
      const drawWheel = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const sectorAngle = (2 * Math.PI) / sectors;

        for (let i = 0; i < sectors; i++) {
          const startAngle = i * sectorAngle;
          const endAngle = (i + 1) * sectorAngle;

          // Draw sector
          ctx.beginPath();
          ctx.moveTo(center.x, center.y);
          ctx.arc(center.x, center.y, radius - 20, startAngle, endAngle);
          ctx.fillStyle = colors[i % 2];
          ctx.fill();
          ctx.closePath();

          // Add gradient stroke on each side
          const createGradient = (angle) => {
            const gradient = ctx.createLinearGradient(
              center.x + Math.cos(angle) * (radius - 20),
              center.y + Math.sin(angle) * (radius - 20),
              center.x,
              center.y
            );
            gradientColors.forEach(({ offset, color }) => gradient.addColorStop(offset, color));
            return gradient;
          };

          ctx.strokeStyle = createGradient(startAngle);
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(
            center.x + Math.cos(startAngle) * (radius - 20),
            center.y + Math.sin(startAngle) * (radius - 20)
          );
          ctx.lineTo(center.x, center.y);
          ctx.stroke();

          ctx.strokeStyle = createGradient(endAngle);
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(
            center.x + Math.cos(endAngle) * (radius - 20),
            center.y + Math.sin(endAngle) * (radius - 20)
          );
          ctx.lineTo(center.x, center.y);
          ctx.stroke();

          // Add text to sector
          ctx.save();
          ctx.textBaseline = 'middle'; // Ensures the text is centered vertically
          ctx.textAlign = 'right';
          ctx.translate(center.x, center.y);
          ctx.rotate((startAngle + endAngle) / 2);
          ctx.fillStyle = i % 2 === 0 ? '#FFC33E' : '#0F41CE';
          ctx.font = 'bold 22px Verdana';
          ctx.fillText(isoStandard(wheelValues[i]), 250, 1);
          ctx.restore();
        }
      };

      const updateWheelTexture = () => {
        const dataURL = canvas.toDataURL('image/png');
        return setTexture(PIXI.Texture.from(dataURL));
      };
      drawWheel();
      updateWheelTexture();
    }, [colors, gradientColors, sectors]);

    useEffect(() => {
      if (wheelRef.current && texture) {
        const sectorAngle = 360 / sectors;
        const targetAngle = 360 * 5 - sectorAngle / 2;
        gsap.killTweensOf(wheelRef.current);
        gsap.to(wheelRef.current, {
          rotation: targetAngle * (Math.PI / 180),
          duration: 0,
          ease: 'linear',
        });
      }
    }, [wheelRef, texture]);

    const spinToSector = useCallback(
      (sectorIndex) => {
        const sectorAngle = 360 / sectors;
        const targetAngle = 360 * 5 + 270 - (sectorIndex * sectorAngle + sectorAngle / 2);
        // soundService.play(SoundTypes.wheelSpinning);
        wheelAnimation = gsap.fromTo(
          wheelRef.current,
          { rotation: 0 },
          {
            rotation: targetAngle * (Math.PI / 180),
            duration: 3,
            repeat: -1,
            ease: 'none',
          }
        );
      },
      [sectors]
    );

    useEffect(() => {
      if (jackPotExpireTime === 0) {
        spinToSector(1);
        gameService.getBoostedJackpot();
      }
    }, [jackPotExpireTime]);

    useEffect(() => {
      if (wheelAnimation && sectorStopIndex !== null) {
        wheelAnimation.kill();
        const sectorAngle = 360 / sectors;
        const targetAngle = 360 * 5 + 270 - (sectorStopIndex * sectorAngle + sectorAngle / 2);
        if (wheelStopAnimationGsap) {
          wheelStopAnimationGsap.kill();
        }
        wheelStopAnimationGsap = gsap.to(wheelRef.current, {
          rotation: targetAngle * (Math.PI / 180),
          duration: 3,
          ease: 'power1.out',
          onComplete: () => {
            setSectorStopIndex(null);
            setIsBoostedJackpotWinPopup(true);
          },
        });
      }
    }, [sectors, sectorStopIndex, setSectorStopIndex, setIsBoostedJackpotWinPopup]);

    const spinToStopSector = useCallback(
      // eslint-disable-next-line consistent-return
      (e) => {
        if (!spinClick) {
          return null;
        }
        soundService.play(SoundTypes.spinWheelButton);
        setSpinClick(false);
        spinToSector(sectorStopIndex);
        if (e) {
          gameService.getBoostedJackpot();
          e.target.interactive = false;
        }
      },
      [spinClick, spinToSector, sectorStopIndex]
    );

    useEffect(() => {
      if (isReplay) {
        setTimeout(() => {
          betReplayService.startReplayBoostedJackpotScene();
          spinToStopSector(null);
        }, 2000);
      }
    }, []);

    useEffect(() => {
      if (wheelLampRef.current) {
        const wheelLamp = new Spine({ key: 'wheelLamp' });
        wheelLamp.play('loop1', true);
        if (isMobile()) {
          wheelLamp.y = -7.5;
        } else {
          wheelLamp.y = 2.5;
          wheelLamp.x = -0.5;
        }
        wheelLampRef.current.addChild(wheelLamp);
      }
    }, [sectors]);

    useEffect(() => {
      if (spinWheelWinAmount && wheelLampRef.current && wheelBlueBoardRef.current) {
        wheelLampRef.current.children[0]?.play('loop2', true);
        wheelBlueBoardRef.current.children[0]?.play('loop2', true);
      }
    }, [spinWheelWinAmount]);

    useEffect(() => {
      if (wheelBlueBoardRef.current) {
        const wheelBlueBoard = new Spine({ key: 'wheelBlueBoard' });
        wheelBlueBoard.play('loop1', true);
        wheelBlueBoardRef.current.addChild(wheelBlueBoard);
      }
    }, []);

    const iChancyLightningAnim = useCallback(
      (wheelBlueBoard) => {
        const lightning = new Spine({ key: 'lightning' });
        lightning.play('idle', true);
        setSpinClick(false);
        setTimeout(() => {
          lightning.play('win', true);
          wheelBlueBoard.play('win', true);
          lightning.addListener(
            'scale',
            () => {
              if (wheelContainerRef.current) {
                gsap.to(wheelContainerRef.current.scale, {
                  x: isMobile() ? 1 : 1.04,
                  y: isMobile() ? 1 : 1.04,
                  duration: 0.2,
                  onComplete: () => {
                    handleWheelScaleDefault(wheelContainerRef);
                  },
                });
              }
            },
            null,
            'win',
            true
          );
          lightning.addListener(
            'complete',
            () => {
              setSpinClick(true);
              wheelBlueBoard.play('idle', true);
              lightning.play('idle', true);
            },
            null,
            'win',
            true
          );
        }, 1500);
        wheelSpinRef.current.addChild(lightning);
      },
      [handleWheelScaleDefault, setSpinClick, wheelContainerRef]
    );

    useEffect(() => {
      if (wheelSpinRef.current) {
        const wheelBlueBoard = new Spine({ key: 'wheelSpinIcon' });
        wheelBlueBoard.play('idle', true);
        if (isIChancy()) {
          iChancyLightningAnim(wheelBlueBoard);
        }
        wheelSpinRef.current.addChild(wheelBlueBoard);
      }
    }, [iChancyLightningAnim]);

    useEffect(() => {
      if (isMobile()) {
        setIsShowBlackLayer(true);
      }
    }, [setIsShowBlackLayer]);

    // eslint-disable-next-line consistent-return
    return (
      <Container>
        <Container ref={wheelBlueBoardRef} />
        {texture && (
          <Sprite ref={wheelRef} anchor={0.5} texture={texture} width={500} height={500} />
        )}
        <Sprite texture={wheelBorder} anchor={0.5} width={550} height={550} />
        <Container ref={wheelLampRef} />
        {!isIChancy() ? <Sprite texture={cursor} anchor={0.5} y={-255} /> : null}
        <Container
          ref={wheelSpinRef}
          cursor='pointer'
          interactive={!isReplay}
          pointerup={(e) => {
            spinToStopSector(e);
          }}
        />
      </Container>
    );
  }
);

export default BoostedJackpotWheel;
